import  {
  IContractDetails
} from "@/types/clients/esquire/requests/payment/extend-reservation-data";
import Reservation from "@/types/clients/esquire/data/general/reservation";
import BookingContract from "@/types/clients/esquire/data/booking/booking-contract";
import {ReservationService} from "@/services/reservation-service";
import dayjs, {Dayjs} from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import Contract from "@/types/clients/esquire/data/general/contract";
import OptionData from "@/types/clients/esquire/data/general/contract-data/option";
import GlobalService from "@/services/global-service";

dayjs.extend(isSameOrBefore);

export class ReservationExtensionService {

  /**
   * Initializes the options selectedDates and isSelected flag.
   */
  static initializeSelectedOptions(
    contracts: Contract[],
    fromDate: Date,
    toDate: Date
  ) {
    contracts.forEach((contract: Contract) => {
      Object.values(contract.options).forEach((option: OptionData) => {
        if (option.isSelectedInitially && !option.isMandatory()) {
          option.isSelected = true;
          option.isSelectedInitially = false;

          option.selectedDates = GlobalService.date().getDatesBetween(fromDate, toDate);
        }
      });
    });
  }


  static updateOptionSelectedDates(
    option: OptionData,
    fromDate: Date,
    toDate: Date
  ) {
    option.selectedDates = GlobalService.date().getDatesBetween(fromDate, toDate);
  }

  static calculateTotalRoomRent(extendData: BookingContract[]): number {
    return extendData.reduce((sum, bookingContract) => {
      return sum + (bookingContract.pricing?.totalPrice ?? 0);
    }, 0);
  }

  static calculateExtraNights(departure: Date | Dayjs |  null | undefined, selected: Date | Dayjs | null | undefined): number {
    if (!departure || !selected) return 0;

    const d1 = dayjs(departure);
    const d2 = dayjs(selected);
    if (!d1.isValid() || !d2.isValid()) return 0;

    return d2.diff(d1, "day");
  }

  static findBookingContractForContract(contract: Contract,extendData: BookingContract[]): BookingContract{
    const result = extendData.find(bc => bc.reference === contract.id);
    if (!result) throw new Error("No BookingContract found for the given contract ID.");
    return result as BookingContract;
  }


  /**
   * Finalizes extension and fetches payment methods.
   */
  static async finalizeExtensionAndGoToPayment(
    reservation: Reservation,
    selectedDate: Date,
    selectedContractIds: Array<string>,
    extendData: Array<BookingContract>) {
    const buildingId = reservation?.building.id;
    if (!buildingId) {
      console.error("No building ID available for the reservation.");
      throw new Error("No building ID available for the reservation.");
    }

    await ReservationService.extendReservation(
      reservation.id,
      selectedContractIds,
      selectedDate.toDateString(),
      this.getContractDetails(reservation, extendData)
    );

    return await GlobalService.payment().fetchPaymentMethods(buildingId);
  }


  /**
   * Get contract details for reservation extension.
   */
  static getContractDetails(reservation: Reservation, extendData: Array<BookingContract>): Record<string, IContractDetails> {
    if (!reservation || !extendData) return {};
    return reservation.contracts.reduce((details, c) => {
      const extendedContract = extendData.find(d => d.reference === c.id);
      const mandatoryServices = extendedContract?.recurringServices
        ?.filter(service => service.isMandatory())
        ?.map(service => service.id) ?? [];

      details[c.id] = {
        price_per_night: extendedContract?.pricing?.pricePerNight ?? -1,
        option_type_ids: [
          ...c.getAddedOptions().map(o => o.id),
          ...mandatoryServices,
          ...c.getRemovedOptions().map(o => o.typeId)
        ]
      };

      return details;
    }, {} as Record<string, IContractDetails>);
  }


}

export default ReservationExtensionService;
